import React, { useCallback, useState } from 'react';
import Autosuggest from 'react-autosuggest';
import Badge from 'react-bootstrap/Badge';
import Form from 'react-bootstrap/Form';
import { useTranslation } from 'react-i18next';
import { v1 as uuidv1 } from 'uuid';

import { resetAbortController } from '../../actions/utils';
import Binance from '../../assets/exchange/binance.svg';
import Bybit from '../../assets/exchange/bybit.svg';
import { uiHelpers } from '../../helpers';
import { useStockSearchHistory } from '../../hooks/useSearchHistory';
import { Category } from '../../py-generated/datalouder/stock/types';
import { miscService } from '../../services';
import { StockSuggestionItem } from '../../shared/types';
import { stockTypeToCategory } from '../stock/stockCategory';

interface Props {
  hideUs?: boolean;
  hideIndex?: boolean;
  value: string;
  onChange: (stockCode: string) => void;
  onChangeStock?: (stock: StockSuggestionItem) => void;
  renderInputComponent?: (
    props: React.InputHTMLAttributes<HTMLInputElement>
  ) => React.ReactNode;
  onKeyPress?: React.KeyboardEventHandler<HTMLInputElement>;
  isValueProducedBySuggestion?: boolean;
  setIsValueProducedBySuggestion?: (val: boolean) => void;
  onSelected?: (stockCode: string, category: Category) => void;
}

const defaultRenderInputComponent = inputProps => (
  <Form.Control {...inputProps} />
);

const shouldRenderSuggestions = (value: string): boolean => {
  return !value || value.trim().length > 0;
};

const StockCodeInput: React.FC<Props> = ({
  value,
  onChange,
  renderInputComponent = defaultRenderInputComponent,
  onKeyPress,
  isValueProducedBySuggestion,
  setIsValueProducedBySuggestion,
  onSelected,
  onChangeStock,
}) => {
  const [suggestions, setSuggestions] = useState<StockSuggestionItem[]>([]);
  const [requestId] = useState(uuidv1());
  const [t] = useTranslation();

  const searchHistory = useStockSearchHistory(value);

  const onSuggestionsFetchRequested = useCallback(
    ({ value }) => {
      const ac = resetAbortController(requestId);
      miscService
        .searchStockCode(ac.signal, value)
        .then(({ candidates }) => {
          setSuggestions(
            candidates.map(candidate => ({
              stockCode: candidate.ticker,
              name_en: candidate.name_en,
              name_zh: candidate.name_zh,
              region: candidate.category == 'HK' ? 'hk' : 'us',
              stockType:
                candidate.category === 'CRPT'
                  ? 'crypto'
                  : candidate.category.toLocaleLowerCase(),
            }))
          );
        })
        .catch(() => setSuggestions([]));
    },
    [requestId]
  );

  const onSuggestionsClearRequested = useCallback(() => setSuggestions([]), []);

  const renderSuggestion = (suggestion: StockSuggestionItem) => {
    return (
      <div style={{ paddingBottom: '16px' }}>
        <div>
          {' '}
          {suggestion.stockType && (
            <Badge className={`badge-` + suggestion.stockType} bg="primary">
              {t('stock-type-' + suggestion.stockType)}
            </Badge>
          )}
          {suggestion.stockType === 'crptbn' ? <img src={Binance} /> : ''}
          {suggestion.stockType === 'crypto' ? <img src={Bybit} /> : ''}{' '}
          <b>{suggestion.stockCode} </b>
        </div>
        <div>
          {' '}
          {suggestion.name_en}
          {suggestion.name_zh === suggestion.name_en
            ? ''
            : ', ' + uiHelpers.tradChify(suggestion.name_zh)}
          {suggestion.stockType === 'crptbn' ? ', Binance' : ''}
          {suggestion.stockType === 'crypto' ? ', Bybit' : ''}
        </div>
      </div>
    );
  };

  const onSuggestionSelected = useCallback(
    (_, { suggestionValue }) => {
      // use to track whether the value is produced by suggestion, function should be provided by parent
      if (
        isValueProducedBySuggestion === false &&
        setIsValueProducedBySuggestion
      ) {
        setIsValueProducedBySuggestion(true);
      }

      onChange(suggestionValue.stockCode);
      onChangeStock?.(suggestionValue);
      if (onSelected) {
        onSelected(
          suggestionValue.stockCode,
          stockTypeToCategory(suggestionValue.stockType)
        );
      }
    },
    [
      isValueProducedBySuggestion,
      onChange,
      onChangeStock,
      onSelected,
      setIsValueProducedBySuggestion,
    ]
  );

  const autosuggestOnChange = useCallback(
    (e, { newValue, method }) => {
      // use to track whether the value is produced by suggestion, function should be provided by parent
      if (
        isValueProducedBySuggestion === true &&
        setIsValueProducedBySuggestion &&
        method !== 'click'
      ) {
        setIsValueProducedBySuggestion(false);
      }
      if (typeof e.target.value === 'string') {
        onChange(e.target.value);
      } else if (typeof newValue === 'string') {
        onChange(newValue);
      }
    },
    [isValueProducedBySuggestion, onChange, setIsValueProducedBySuggestion]
  );

  return (
    <Autosuggest
      inputProps={{
        value,
        onChange: autosuggestOnChange,
        className: 'stock-input',
        onKeyPress: onKeyPress,
        placeholder: t('input-stock-name-or-code'),
      }}
      suggestions={value ? suggestions : searchHistory}
      onSuggestionsFetchRequested={onSuggestionsFetchRequested}
      onSuggestionsClearRequested={onSuggestionsClearRequested}
      renderSuggestion={renderSuggestion}
      getSuggestionValue={x => x}
      onSuggestionSelected={onSuggestionSelected}
      renderInputComponent={renderInputComponent}
      shouldRenderSuggestions={shouldRenderSuggestions}
    />
  );
};

export default StockCodeInput;
